import type { DateRange } from "@/features/admin-dashboard/utils/date-ranges"

import { api } from "@/lib/api-client"

import { buildDateRangeQuery } from "./_shared"

export type TokenUsageByModel = {
  model: string
  input_tokens: number
  output_tokens: number
  reasoning_tokens: number
  cached_tokens: number
  total_tokens: number
  request_count: number
  cached_percentage: number
  estimated_cost_usd: number
}

/**
 * Hosted agents as their own cost-center dimension (HERMES Fas 6). Kept
 * separate from `by_model`/totals — run usage is SEK-native, has no
 * per-model split and selftest runs are excluded. Shape:
 * services.hosted_agents.usage_analytics.usage_totals().
 */
export type HostedAgentsTokenUsage = {
  run_count: number
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  cost_sek: number
  selftest_runs: number
  by_agent: Array<{
    agent_id: string
    name: string | null
    run_count: number
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    cost_sek: number
  }>
}

export type AdminTokenMetricsResponse = {
  total_input_tokens: number
  total_output_tokens: number
  total_reasoning_tokens: number
  total_cached_tokens: number
  total_tokens: number
  total_requests: number
  by_model: TokenUsageByModel[]
  cache_efficiency: number
  total_cost_usd: number
  /** Optional: absent on cached payloads written before Fas 6; may be {}. */
  hosted_agents?: Partial<HostedAgentsTokenUsage>
}

export async function getTokenMetrics(
  dateRange: DateRange | 7 | 30,
  currency: "SEK" | "USD" | "EUR" = "USD"
): Promise<AdminTokenMetricsResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/token-metrics?${queryString}&currency=${currency}`)
  return response.data || response
}

export type AdminCostsResponse = {
  rangeDays: number
  services: Array<{
    service: string
    amount: number
    changePct: number
  }>
  total: number
  changePct: number
}

export async function getCosts(dateRange: DateRange | 7 | 30): Promise<AdminCostsResponse> {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/costs?${queryString}`)
  return response.data || response
}

export type AdminCostsByUserResponse = {
  rangeDays: number
  currency: "SEK" | "USD" | "EUR"
  summary: {
    totalUsers: number
    totalCost: number
    totalTokens: number
    totalRequests: number
    totalErrors: number
  }
  users: Array<{
    userId: string
    alias: string | null
    profile: {
      name: string | null
      email: string | null
      jobTitle: string | null
      department: string | null
      officeLocation: string | null
      company: string | null
    }
    usage: {
      requests: number
      tokensIn: number
      tokensOut: number
      tokensTotal: number
      errors: number
      cost: number
      lastActivity: string | null
    }
  }>
}

export async function getCostsByUser(
  dateRange: DateRange | 7 | 30,
  currency: "SEK" | "USD" | "EUR" = "USD"
): Promise<AdminCostsByUserResponse> {
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const queryString = buildDateRangeQuery(range)
  const response = await api.get(`/admin/costs/users?${queryString}&currency=${currency}`)
  return response.data || response
}
